import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { HttpService, ModalService, NavigationService } from 'app/services';
import { Subject } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { locale as english } from './i18n/en';
import { AwsService } from 'app/services/aws.service';

interface DeviceTileData {
  title: string;
  editable?: boolean;
  editing?: boolean;
  key?: string;
  icon?: string;
  badge?: any;
  data: any;
  clickable?: boolean;
  clickHandler?: Function;
}

@Component({
  selector: 'app-device-details',
  templateUrl: './device-details.component.html',
  styleUrls: ['./device-details.component.scss'],
})
export class DeviceDetailsComponent implements OnDestroy, OnInit {
  @Input() serial: string;
  @Input() device: any;
  @Output() galleryEvent = new EventEmitter<string>();
  private _unsubscribeAll: Subject<any>;
  urlParams: any = new URLSearchParams(window.location.search);
  deviceTiles: DeviceTileData[] = [];
  curTime: number = new Date().getTime();
  showGallery: boolean;
  gallerySrc: string;
  embedded: boolean = !window.location.href.includes('/devices/details');
  loading = true;
  img: any;
  prevDevicePic: any;

  constructor(
    private _fuseTranslationLoaderService: FuseTranslationLoaderService,
    public httpService: HttpService,
    private datePipe: DatePipe,
    private navigationService: NavigationService,
    private modalService: ModalService,
    private aws: AwsService,
  ) {
    this._fuseTranslationLoaderService.loadTranslations(english);
    this._unsubscribeAll = new Subject();
  }

  refreshInterval: any;

  ngOnInit(): void {
    if (!this.serial)
      this.serial = this.urlParams.get('serial') || this.device?.serial;
    if (this.device)
      this.buildDeviceTiles();
    else {
      this.getDevice();
      this.refreshInterval = setInterval(this.getDevice.bind(this), 10000);
    }
  }

  getDevice(): void {
    //ambiguified (tm) devices are going to simplify so much code around the EP once fully implemented
    this.httpService.getAmbiguousBusinessDevice(this.serial).pipe(
      catchError(err => { throw err })
    ).subscribe(device => {
      this.device = device;
      this.buildDeviceTiles();
    });
  }

  ngOnDestroy(): void {
    clearInterval(this.refreshInterval);
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  async buildDeviceTiles(): Promise<boolean> {
    console.log(this.device);
    let promises = [];

    if (!this.deviceTiles)
      this.deviceTiles = [];

    /*~~ promise 1/3: devicepic tile? ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
    if (this.prevDevicePic !== this.device.devicepic) {
      this.prevDevicePic = this.device.devicepic;
      if (this.device.devicepic && this.device.devicepic.indexOf('assets/imgs') == -1) {
        promises.push(this.aws.getSignedFileRequest(this.device.devicepic).toPromise().then(async img => {
          if (img.includes('.txt?')) {
            const imgData = await fetch(img);
            this.img = await imgData.text();
          }
        }));
      }
    }

    /*~~ promise 2/3: polling_data tiles? ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
    if (this.device.type == 'THW-PIN' || this.device.type == 'Battery') {
      promises.push(this.httpService.getSensorPollingData(this.device.id, { limit: 1, order: 'occurred DESC' }).then(pd => {
        if (this.device.type == 'THW-PIN') {
          let re = pd;
          if (re && re.length > 0) {
            let lastHumidity = parseFloat(re[0].med);
            let lastTemp = parseFloat(re[0].min);
            let degreeType = "F";
            lastTemp = Math.round(Number(re[0].min) * 1.8) + 32;

            let tempTile = this.deviceTiles.find(el => el.key == 'temperature');
            let tempData = `${lastTemp}°${degreeType}`;
            if (!tempTile)
              this.deviceTiles.push({
                key: 'temperature',
                title: 'Temperature',
                icon: 'fa fa-temperature-high',
                data: tempData,
                clickable: true,
                clickHandler: this.showAlertThresholdModal.bind(this)
              })
            else if (tempTile.data !== tempData)
              tempTile.data = tempData;

            let humidTile = this.deviceTiles.find(el => el.key == 'humidity');
            let humidData = `${lastHumidity}%`;
            if (!humidTile)
              this.deviceTiles.push({
                key: 'humidity',
                title: 'Humidity',
                icon: 'fa fa-tint',
                data: humidData,
                clickable: true,
                clickHandler: this.showAlertThresholdModal.bind(this)
              });
            else if (humidTile.data !== humidData)
              humidTile.data = humidData;
          }
        }

        if (this.device.type == 'Battery') {
          if (pd[0] && pd[0].min) {
            let batTile = this.deviceTiles.find(el => el.key == 'battery');
            let batData = `${parseFloat(pd[0].min).toFixed(2)}v`;
            if (!batTile)
              this.deviceTiles.push({
                key: 'battery',
                title: 'Battery Level',
                icon: 'fa fa-chart-line',
                data: batData,
                clickable: true,
                clickHandler: this.showAlertThresholdModal.bind(this)
              });
            else if (batTile.data !== batData)
              batTile.data = batData;
          }
        }
      }));
    }

    /*~~ promise 3/3: the tiles that dont require additional network resources :^)  ~~~~~~~~~~~~~~~~~*/
    promises.push(new Promise(res => {
      //--sensors -- model specific tiles -------------------------------------------------------------------
      if (this.device.type == 'BilgeHW' || this.device.type == 'THW-PIN') {
        let waterTile = this.deviceTiles.find(el => el.key == 'water'),
          waterData = this.device.sensr_water ? 'Wet' : 'Dry';
        if (!waterTile)
          this.deviceTiles.push({
            key: 'water',
            title: 'Water Sensor',
            icon: 'fa fa-water',
            data: waterData
          });
        else if (waterTile.data !== waterData)
          waterTile.data = waterData;
      }

      if (this.device.type == 'Sound') {
        let tile = this.deviceTiles.find(el => el.key == 'sound'),
          data = this.device.app_tc ? 'Alert' : 'Ready';
        if (!tile)
          this.deviceTiles.push({
            key: 'sound',
            title: 'Sound Sensor',
            icon: 'fa fa-waveform',
            data: data
          });
        else if (tile.data !== data)
          tile.data = data;
      }

      if (this.device.type == 'BilgeHW' || this.device.type == 'Bilge') {
        let tile = this.deviceTiles.find(el => el.key == 'bilge'),
          data = this.device.power_detection ? 'On' : 'Off';
        if (!tile)
          this.deviceTiles.push({
            key: 'bilge',
            title: 'Bilge Pump',
            icon: 'fa fa-faucet',
            data: data
          });
        else if (tile.data !== data)
          tile.data = data;
      }

      if (this.device.type == 'Shore') {
        let tile = this.deviceTiles.find(el => el.key == 'shore'),
          data = this.device.power_detection ? 'On' : 'Off'
        if (!tile)
          this.deviceTiles.push({
            key: 'shore',
            title: 'Shore State',
            icon: 'fa fa-plug',
            data: this.device.power_detection ? 'On' : 'Off'
          });
        else if (tile.data !== data)
          tile.data = data;
      }

      if (/Trap|Mouse|Kiwi|Reed.*/.test(this.device.type)) {
        let tile = this.deviceTiles.find(el => el.key == 'reed'),
          data = this.device.reedStatus ? 'On' : 'Off',
          icon = `fa ${data == 'On' ? 'fa-link' : 'fa-unlink'}`;
        if (!tile)
          this.deviceTiles.push({
            key: 'reed',
            title: 'Reed Status',
            icon: icon,
            data: data
          });
        else if (tile.data !== data) {
          tile.data = data;
          tile.icon = icon;
        }
      }

      //--sensors -- generic tiles --------------------------------------------------------------------------
      if (this.device.deviceType == 'sensor') {
        let rssiTile = this.deviceTiles.find(el => el.key == 'rssi'),
          rssiData = this.device.sensr_rssi;
        if (!rssiTile)
          this.deviceTiles.push({
            key: 'rssi',
            title: 'Sensor RSSI',
            icon: 'fa fa-signal-stream',
            data: rssiData
          });
        else if (rssiTile.data !== rssiData)
          rssiTile.data = rssiData;

        let sensitivityTile = this.deviceTiles.find(el => el.key == 'sensitivity'),
          sensitivityData = this.device.sensitivity_changed ? `${this.device.sensitivity_change_value} (Pending)` : this.device.sensr_setting,
          sensitivityIcon = `fa fa-sliders-h ${this.device.sensitivity_changed ? 'pending' : ''}`;
        if (!sensitivityTile) {
          let sensitivityOpts: DeviceTileData = {
            key: 'sensitivity',
            title: 'Sensor Setting',
            icon: sensitivityIcon,
            data: sensitivityData
          };
          if (this.device.type == 'PIR' || this.device.type == 'SpotPIR') {
            sensitivityOpts.title = 'Sensor Sensitivity';
            sensitivityOpts.clickable = true;
            sensitivityOpts.clickHandler = this.showSensorSensitivityModal.bind(this)
          }
          this.deviceTiles.push(sensitivityOpts);
        } else if (sensitivityTile.data !== sensitivityData) {
          sensitivityTile.data = sensitivityData;
          sensitivityTile.icon = sensitivityIcon;
        }
      }

      //--devices -- model specific tiles -------------------------------------------------------------------
      if (this.device.serial.match(/SHH.*/)) {
        let hubSensitivityTile = this.deviceTiles.find(el => el.key == 'hubSensitivity'),
          hubSensitivityData = this.device.issensitivityon ? this.device.sensor_adjust : 'Disabled';
        if (!hubSensitivityTile)
          this.deviceTiles.push({
            key: 'hubSensitivity',
            title: 'Sensitivity',
            icon: 'fa fa-sliders-h',
            data: hubSensitivityData
          });
        else if (hubSensitivityTile.data !== hubSensitivityData)
          hubSensitivityTile.data = hubSensitivityData;
      }

      //--devices -- generic tiles --------------------------------------------------------------------------
      if (this.device.deviceType == 'device') {
        let limitTile = this.deviceTiles.find(el => el.key == 'limiter'),
          limitBadge = this.device.st_dtc,
          limitIcon = `fa ${this.device.bsm_dc === 1 ? 'fa-bell-on' : 'fa-bell'}`,
          limitData = this.getAlertLimiter();
        if (!limitTile)
          this.deviceTiles.push({
            key: 'limiter',
            title: 'Alert Limiter',
            icon: limitIcon,
            badge: limitBadge,
            data: limitData,
          });
        else if (limitTile.badge !== limitBadge || limitTile.data !== limitData) {
          limitTile.badge = limitBadge;
          limitTile.data = limitData;
          limitTile.icon = limitIcon;
        }
      }

      //--universal tiles -- applicable to each and every unit, all across our great big world :) -----------
      let lastActiveTile = this.deviceTiles.find(el => el.key == 'lastActive'),
        lastActiveData = (this.device.lastActive ? this.datePipe.transform(this.device.lastActive, 'EE M/d, h:mm a') : 'N/A');
      if (!lastActiveTile)
        this.deviceTiles.push({
          key: 'lastActive',
          title: 'Last Active',
          icon: 'fa fa-running',
          data: lastActiveData
        });
      else if (lastActiveTile.data !== lastActiveData)
        lastActiveTile.data = lastActiveData;

      let notesTile = this.deviceTiles.find(el => el.key == 'notes'),
        notesData = this.device.notes || 'No device notes';
      if (!notesTile)
        this.deviceTiles.push({
          key: 'notes',
          title: 'Device Notes',
          icon: 'fa fa-comment-lines',
          clickable: true,
          clickHandler: this.editDeviceNotes.bind(this),
          data: notesData
        });
      else if (notesTile.data !== notesData)
        notesTile.data = notesData;

      let pauseTile = this.deviceTiles.find(el => el.key == 'pause'),
        pauseData = this.getPauseTimer();
      if (!pauseTile)
        this.deviceTiles.push({
          key: 'pause',
          title: 'Pause Timer',
          icon: 'fa fa-pause-circle',
          data: pauseData
        });
      else if (pauseTile.data !== pauseData)
        pauseTile.data = pauseData;

      let hbTile = this.deviceTiles.find(el => el.key == 'heartbeat'),
        hbData = this.getHeartbeat(),
        hbIcon = `fa heartbeat-icon ${hbData == 'Good heartbeat' ? 'fa-heart' : 'fa-heart-broken'}`;
      if (!hbTile)
        this.deviceTiles.push({
          key: 'heartbeat',
          title: 'Last Heartbeat',
          icon: hbIcon,
          data: hbData
        });
      else if (hbTile.data !== hbData) {
        hbTile.data = hbData;
        hbTile.icon = hbIcon;
      }

      let eventTile = this.deviceTiles.find(el => el.key == 'events'),
        eventBadge = this.getDeviceLog();
      if (!eventTile)
        this.deviceTiles.push({
          key: 'events',
          title: 'Device Log',
          icon: 'fa fa-history',
          badge: eventBadge,
          data: 'View 30 Day Device Log',
          clickable: true,
          clickHandler: this.showDeviceLogModal.bind(this)
        });
      else if (eventTile.badge !== eventBadge)
        eventTile.badge = eventBadge;

      let segpestTile = this.deviceTiles.find(el => el.key == 'segpest');
      if (!segpestTile)
        this.deviceTiles.push({
          key: 'segpest',
          title: 'Deployment History',
          icon: 'fa fa-crosshairs',
          data: 'View Deployment History',
          clickable: true,
          clickHandler: this.showDeploymentHistoryModal.bind(this)
        });

      let batteryTile = this.deviceTiles.find(el => el.key == 'sensor-battery'),
        batteryData = this.getBatteryData();
      if (!batteryTile)
        this.deviceTiles.push({
          key: 'sensor-battery',
          title: 'Sensor Battery',
          icon: 'fa fa-bolt',
          data: batteryData
        });
      else if (batteryTile.data !== batteryData)
        batteryTile.data = batteryData;

      if (this.device.type == 'Insect') {
        const ipdTile = this.deviceTiles.find(el => el.key == 'ipd-data');

        if (!ipdTile) {
          this.deviceTiles.push({
            clickHandler: () => this.modalService.showIpdData(this.device.id),
            data: 'View Detection Records',
            title: 'Insect Detection',
            icon: 'fa fa-bugs',
            key: 'ipd-data',
            clickable: true,
          });
        }
      }

      res(true);
    }));

    return Promise.all(promises).then(() => {
      if (this.loading)
        this.loading = false;
      return true;
    })
  }

  getBatteryData(): string {
    let volts = this.device.device_volts || this.device.sensr_volts;
    return volts ? `${volts} volts` : 'Not Specified';
  }

  getPauseTimer(): any {
    const disarmTime = this.device.disarmTime;
    return disarmTime && disarmTime > this.curTime ? (disarmTime - this.curTime) : 0;
  }

  getHeartbeat(): string {
    if (!this.device.power)
      return 'Powered off';
    if (!this.device.lastConnection)
      return 'Waiting for first heartbeat';
    if (!this.device.heartbeat || this.device.heartbeat === '000')
      return 'Good heartbeat';

    let hbTime = Number(this.device.heartbeat) * 60 * 60 * 1000;

    if (this.serial.substring(0, 3) === 'SHH')
      hbTime = ((3600000 + (hbTime * 1.1)) + this.device.lastConnection);
    else //Sensors
      hbTime = ((32400000 + (hbTime * 1.1)) + this.device.lastConnection);

    if (hbTime < new Date().getTime())
      return 'Missed heartbeat';

    return 'Good heartbeat';
  }

  getDeviceLog(): any {
    if (this.device.events)
      return this.device.events.filter(e => {
        let eventDate: any = new Date(e.created);
        return ((this.curTime - eventDate) / 86400000 <= 30);
      }).length;;

    return 0;
  }

  getAlertLimiter(): string {
    let val = this.device.bsm_dc ? this.device.bsm_delay : this.device.st_delay;
    return `${val} Minute${val > 1 ? 's' : ''}`;
  }

  goBack(): void {
    this.navigationService.goBack();
  }

  showDeviceLogModal(): void {
    this.modalService.showDeviceLogModal(this.device.serial, this.device.events);
  }

  showDeploymentHistoryModal(): void {
    this.modalService.showDeploymentHistoryModal(this.device);
  }

  showAlertThresholdModal(): void {
    this.modalService.showAlertThresholdModal(this.device);
  }

  editDeviceNotes(): void {
    let continueFn = async (vals) => {
      let err;
      if (vals?.device_notes !== this.device.notes)
        await this.httpService.patchAmbiguousDevice(this.device, { notes: vals?.device_notes || '' }).toPromise().then((res: any) =>
          res?.count === 1 ?
            this.device.notes = (vals?.device_notes || '') :
            err = `Failed to save changes to device ${this.device.serial}`
        ).catch(e => (err = e));

      if (err) {
        this.modalService.errorDialog(err);
        throw err;
      }
    },
      modalOpts = {
        title: 'Edit device notes',
        cancellable: true,
        inputs: [{
          type: 'textarea',
          value: this.device.notes,
          placeholder: 'Add notes to this device',
          name: 'device_notes'
        }],
        minWidth: '35vw',
        continueFn: continueFn
      };

    this.modalService.showInputModal(modalOpts).afterClosed().subscribe(res => {
      if (res !== undefined) {
        this.device.notes = res?.device_notes;
        this.buildDeviceTiles();
      }
    });
  }

  showSensorSensitivityModal(): void {
    this.modalService.showSensorSensitivityModal(this.device).afterClosed().subscribe((res?: any) => {
      if (res?.sensitivity_changed && res?.sensitivity_change_value !== this.device.sensr_setting) {
        this.device.sensitivity_changed = res?.sensitivity_changed;
        this.device.sensitivity_change_value = res?.sensitivity_change_value;
        this.buildDeviceTiles();
      }
    });
  }

  openGallery(src) {
    this.modalService.showGallery(src);
  }

  showImageViewer(): void {
    this.modalService.showImageViewer(this.device);
  }
}
